import * as React from 'react';
import { styled } from '@mui/material/styles';
import { findIndex } from 'lodash';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { sdgLogo } from 'kosmos/images';

interface StyledTabProps {
  label: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const generateItem = (title: string): JSX.Element => {
  return <div>{title}</div>;
};

const learnMoreLink = (link: string): JSX.Element => {
  return (
    <Link href={link} target="_blank">
      Learn more
    </Link>
  );
};

const schema = [
  {
    id: 'sdg',
    title: 'Sustainable Development Goals',
    content: (
      <div>
        <img src={sdgLogo} width="400" />
        <h4>Sustainable Development Goals (SDGs).</h4>
        <p>
          The Sustainable Development Goals (SDGs) or Global Goals are a collection of 17
          interlinked global goals designed to be a “blueprint to achieve a better and more
          sustainable future for all”. The SDGs were set up in 2015 by the United Nations General
          Assembly (UN-GA) and are intended to be achieved by 2030. They are included in a UN-GA
          Resolution called the 2030 Agenda or what is colloquially known as Agenda 2030. The SDGs
          were developed in the Post-2015 Development Agenda as the future global development
          framework to succeed the Millennium Development Goals which ended in 2015.
        </p>
        <div>The 17 SDGs are: </div>
        {generateItem('(1) No Poverty,')}
        {generateItem('(2) Zero Hunger,')}
        {generateItem('(3) Good Health and Well-being,')}
        {generateItem('(4) Quality Education,')}
        {generateItem('(5) Gender Equality,')}
        {generateItem('(6) Clean Water and Sanitation,')}
        {generateItem('(7) Affordable and Clean Energy,')}
        {generateItem('(8) Decent Work and Economic Growth,')}
        {generateItem('(9) Industry, Innovation and Infrastructure,')}
        {generateItem('(10) Reduced Inequality,')}
        {generateItem('(11) Sustainable Cities and Communities,')}
        {generateItem('(12) Responsible Consumption and Production,')}
        {generateItem('(13) Climate Action,')}
        {generateItem('(14) Life Below Water,')}
        {generateItem('(15) Life On Land,')}
        {generateItem('(16) Peace, Justice, and Strong Institutions,')}
        {generateItem('(17) Partnerships for the Goals.')}
        <div>{learnMoreLink('https://en.wikipedia.org/wiki/Sustainable_Development_Goals')}</div>
      </div>
    ),
  },
  {
    id: 'dug',
    title: 'Goods and Services Check',
    content: (
      <div>
        <h4>Goods and Services Check</h4>
        <p>
          In politics, diplomacy and export control, “dual-use” refers to technology that can be
          used for both peaceful and military aims.
        </p>
        <p>
          More generally speaking, dual-use can also refer to any technology which can satisfy more
          than one goal at any given time. Thus, expensive technologies that would otherwise benefit
          only civilian commercial interests can also be used to serve military purposes if they are
          not otherwise engaged, such as the Global Positioning System.
        </p>
        <p>Source: EU TARICS (Integrated Tariff of the European Communities)</p>
        <div>{learnMoreLink('https://en.wikipedia.org/wiki/Dual-use_technology')}</div>
      </div>
    ),
  },
  {
    id: 'illegal-logging-check',
    title: 'Illegal Logging Check',
    content: (
      <div>
        <h4>Illegal Logging Check</h4>
        <p>
          Illegal logging is the harvesting of timber in contravention of the laws and regulations
          of the country of harvest. In economic terms illegal logging results in lost revenues and
          other foregone benefits. In environmental terms illegal logging is associated with
          deforestation, climate change and a loss of biodiversity. In social terms illegal logging
          can be linked to conflicts over land and resources, the disempowerment of local and
          indigenous communities, corruption and armed conflicts.
        </p>
        <p>Source: FLEG, FLEGT (Forest Law Enforcement, Governance and Trade)</p>
      </div>
    ),
  },
  {
    id: 'eu',
    title: 'EU Taxonomy Regulation',
    content: (
      <div>
        <h4>EU Taxonomy Regulation</h4>
        <p>
          The EU taxonomy for sustainable activities (i.e. “green taxonomy”) is a classification
          system established to clarify which investments are environmentally sustainable, in the
          context of the European Green Deal. The aim of the taxonomy is to prevent greenwashing and
          to help investors make greener choices. Investments are judged by six objectives: climate
          change mitigation, climate change adaptation, the circular economy, pollution, effect on
          water, and biodiversity. The taxonomy came into force in July 2020. The UK is working on
          its own separate taxonomy.
        </p>

        <div>
          {learnMoreLink('https://en.wikipedia.org/wiki/EU_taxonomy_for_sustainable_activities')}
        </div>
      </div>
    ),
  },
  {
    id: 'dnsh',
    title: 'Do No Significant Harm (DNSH)',
    content: (
      <div>
        <h4>EU Taxonomy Regulation - Do No Significant Harm (DNSH)</h4>
        <p>
          For an economic activity be certified as being environmentally sustainable in keeping with
          respect to the Taxonomy, substantial contribution must be made to at least one of the
          abovementioned objectives and simultaneously have no significant detrimental impact on the
          other five. The principle in accordance with this is called ‘Do No Significant Harm’
          (DNSH). Aside the 6 environmental objectives, the EU taxonomy also set forth four (4)
          requirements to be met by an economic activity in order to be taxonomy oriented:
        </p>

        <ul>
          <li>making a substantial contribution to at least one environmental objective.</li>

          <li>doing no significant harm to any other environmental objective.</li>

          <li>complying with minimum social safeguards.</li>

          <li>complying with the technical screening criteria.</li>
        </ul>
        <div>
          {learnMoreLink(
            'https://ec.europa.eu/finance/docs/level-2-measures/taxonomy-regulation-delegated-act-2021-2800-impact-assessment_en.pdf',
          )}
        </div>
      </div>
    ),
  },
  {
    id: 'sfdr',
    title: 'Sustainable Finance Disclosure Regulation',
    content: (
      <div>
        <h4>Sustainable Finance Disclosure Regulation</h4>
        <p>
          The European Commission adopted on 6 of April a technical standards to be used by
          financial market participants when disclosing sustainability-related information under the
          Sustainable Finance Disclosures Regulation (SFDR). Today’s Delegated Regulation specifies
          the exact content, methodology and presentation of the information to be disclosed,
          thereby improving its quality and comparability. Under these rules, financial market
          participants will provide detailed information about how they tackle and reduce any
          possible negative impacts that their investments may have on the environment and society
          in general. Moreover, these new requirements will help to assess the sustainability
          performances of financial products. Compliance with sustainability-related disclosures
          will contribute to strengthening investor protection and reduce greenwashing. This will
          ultimately support the financial system’s transition towards a more sustainable economy.
        </p>
        <div>
          {learnMoreLink(
            'https://ec.europa.eu/finance/docs/level-2-measures/C_2022_1931_1_EN_ACT_part1_v6%20(1).pdf',
          )}
        </div>
      </div>
    ),
  },
  {
    id: 'ghg',
    title: 'Greenhouse Gas emissions',
    content: (
      <div>
        <h4>Greenhouse Gas emissions</h4>
        <p>
          Greenhouse gas emissions are categorised into three groups or &lsquo;Scopes&rsquo; by the
          most widely-used international accounting tool, the Greenhouse Gas (GHG) Protocol. Scope 1
          covers direct emissions from owned or controlled sources. Scope 2 covers indirect
          emissions from the generation of purchased electricity, steam, heating and cooling
          consumed by the reporting company. Scope 3 includes all other indirect emissions that
          occur in a company’s value chain.
        </p>
        <div>
          {learnMoreLink(
            'https://www.carbontrust.com/resources/briefing-what-are-scope-3-emissions',
          )}
        </div>
      </div>
    ),
  },
];

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{
        paddingLeft: 15,
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Divider sx={{ borderWidth: 1 }} />
      {value === index && (
        <Box sx={{ pt: 0, overflowX: 'hidden' }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const AntTabs = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#1890ff',
    left: 0,
    width: 20,
  },
});

const AntTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  width: 400,
  alignItems: 'flex-start',
  textAlign: 'left',
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  fontWeight: theme.typography.fontWeightBold,
  paddingLeft: 40,
  margin: '5px 0',
  color: 'black',
  border: '2px solid #757575',
  '&:hover': {
    color: '#40a9ff',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: '#1890ff',
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff',
  },
}));

export default function LearnMore({ initialTabId }: { initialTabId?: string }) {
  const [tabId, setTabId] = React.useState(0);

  React.useEffect(() => {
    const index = findIndex(schema, (e) => e.id === initialTabId);
    setTabId(index);
  }, [initialTabId]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabId(newValue);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: 'background.paper',
        display: 'flex',
        height: 524,
      }}
    >
      <AntTabs
        orientation="vertical"
        variant="scrollable"
        value={tabId}
        onChange={handleChange}
        aria-label="Vertical tabs example"
      >
        {schema.map((s, i) => (
          <AntTab key={i} label={s.title} {...a11yProps(i)} />
        ))}
      </AntTabs>
      {schema.map((s, i) => (
        <TabPanel key={i} value={tabId} index={i}>
          {s.content}
        </TabPanel>
      ))}
    </Box>
  );
}
